import React, { useState, useRef, useEffect } from 'react';
import { Claim } from '../api/claims';
import { draftEmailWithAI } from '../api/ai';
import './ComplexAIButton.css';

interface ComplexAIButtonProps {
  /**
   * Called with the generated draft text. Parent decides how to inject it
   * (replace body, insert at cursor, etc.) via `onInsert` / `onReplace`.
   */
  onInsert: (draftText: string) => void;
  /**
   * Optional: called when user clicks "Replace" instead of "Insert".
   * If not supplied, only Insert is available.
   */
  onReplace?: (draftText: string) => void;
  /** Current content of the editor, passed as context to the LLM for refinement */
  existingBody?: string;
  /** Claim context (used in system prompt) */
  claim?: Partial<Claim> | null;
  /** "email" or "letter" — tailors the LLM prompt */
  draftType?: 'email' | 'letter';
  /** Optional summary of prior correspondence thread */
  threadSummary?: string;
  /** Disable the whole button (e.g. while sending) */
  disabled?: boolean;
}

const ComplexAIButton: React.FC<ComplexAIButtonProps> = ({
  onInsert,
  onReplace,
  existingBody,
  claim,
  draftType = 'email',
  threadSummary,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        // Don't auto-close if we have a draft showing — user needs to decide
        if (!draft && !loading) {
          setOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, draft, loading]);

  const reset = () => {
    setPrompt('');
    setDraft(null);
    setError(null);
    setLoading(false);
  };

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  const handleGenerate = async () => {
    const trimmed = prompt.trim();
    if (!trimmed) {
      setError('Please enter what you want the AI to draft.');
      return;
    }
    setLoading(true);
    setError(null);
    setDraft(null);
    try {
      const result = await draftEmailWithAI({
        prompt: trimmed,
        draftType,
        claim: claim || undefined,
        existingBody: existingBody || undefined,
        threadSummary: threadSummary || undefined,
      });
      if (result.success && result.draft) {
        setDraft(result.draft);
      } else {
        setError('The AI did not return a draft. Try rephrasing your prompt.');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to generate draft';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleInsert = () => {
    if (draft) {
      onInsert(draft);
      handleClose();
    }
  };

  const handleReplace = () => {
    if (draft && onReplace) {
      onReplace(draft);
      handleClose();
    }
  };

  const handleRegenerate = () => {
    setDraft(null);
    setError(null);
  };

  return (
    <div className="complex-ai-wrapper">
      <button
        type="button"
        className="complex-ai-button"
        onClick={handleOpen}
        disabled={disabled}
        title="Draft with Complex AI"
      >
        <span className="complex-ai-icon">✨</span>
        <span>Complex AI</span>
      </button>

      {open && (
        <div className="complex-ai-popover" ref={popoverRef}>
          <div className="complex-ai-popover-header">
            <strong>Draft with Complex AI</strong>
            <button
              type="button"
              className="complex-ai-close"
              onClick={handleClose}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {!draft && (
            <>
              <label className="complex-ai-label">
                What should this {draftType} say?
              </label>
              <textarea
                className="complex-ai-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={
                  draftType === 'letter'
                    ? 'e.g. "Draft a letter to the lender formally requesting a copy of the credit agreement under section 77 CCA 1974"'
                    : 'e.g. "Acknowledge the lender\'s response and ask for the missing agreement number"'
                }
                rows={4}
                disabled={loading}
                autoFocus
              />
              {existingBody && existingBody.trim() && (
                <p className="complex-ai-hint">
                  The AI will see your current draft and can refine it.
                </p>
              )}
              {error && <div className="complex-ai-error">{error}</div>}
              <div className="complex-ai-actions">
                <button
                  type="button"
                  className="complex-ai-cancel"
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="complex-ai-generate"
                  onClick={handleGenerate}
                  disabled={loading || !prompt.trim()}
                >
                  {loading ? 'Drafting…' : 'Generate Draft'}
                </button>
              </div>
            </>
          )}

          {draft && (
            <>
              <label className="complex-ai-label">Generated draft (preview)</label>
              <div className="complex-ai-preview">{draft}</div>
              {error && <div className="complex-ai-error">{error}</div>}
              <div className="complex-ai-actions complex-ai-actions-review">
                <button
                  type="button"
                  className="complex-ai-cancel"
                  onClick={handleRegenerate}
                >
                  ← New prompt
                </button>
                <button
                  type="button"
                  className="complex-ai-insert"
                  onClick={handleInsert}
                  title="Insert the draft at the cursor position"
                >
                  Insert
                </button>
                {onReplace && (
                  <button
                    type="button"
                    className="complex-ai-replace"
                    onClick={handleReplace}
                    title="Replace the entire current draft with this"
                  >
                    Replace all
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ComplexAIButton;
